/**
 * usePollingInterval — persisted polling interval selector.
 *
 * Stores the user's preferred polling interval in localStorage and returns a
 * tuple of [currentInterval, setInterval] that components can use to control
 * TanStack Query's refetchInterval.
 *
 * Business rules (BR-U5-17, BR-U5-18, BR-U5-19):
 *   - Valid values: 15 000 ms, 30 000 ms, 60 000 ms, 300 000 ms
 *   - Any stored value NOT in the valid set is silently replaced with DEFAULT_INTERVAL
 *   - Setting an invalid value via setInterval is a no-op (guard enforced)
 *   - Persistence key: 'sm_pollingInterval' in localStorage
 *   - Default: 30 000 ms (30 s)
 */
import { useState } from 'react';

export const VALID_INTERVALS = [15_000, 30_000, 60_000, 300_000] as const;
export type PollingInterval = (typeof VALID_INTERVALS)[number];

const DEFAULT_INTERVAL: PollingInterval = 30_000;
const STORAGE_KEY = 'sm_pollingInterval';

/** Returns the stored interval if valid, or the default if invalid/missing (BR-U5-18). */
function readStoredInterval(): PollingInterval {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null) return DEFAULT_INTERVAL;
  const parsed = parseInt(raw, 10);
  return (VALID_INTERVALS as readonly number[]).includes(parsed)
    ? (parsed as PollingInterval)
    : DEFAULT_INTERVAL;
}

/**
 * Returns the current polling interval and a setter that persists to localStorage.
 *
 * @returns [interval, setInterval] — interval is always a valid PollingInterval value
 */
export function usePollingInterval(): [PollingInterval, (ms: number) => void] {
  const [interval, setIntervalState] = useState<PollingInterval>(readStoredInterval);

  const setInterval = (ms: number): void => {
    // Guard: silently ignore invalid values (BR-U5-18)
    if (!(VALID_INTERVALS as readonly number[]).includes(ms)) return;
    const valid = ms as PollingInterval;
    localStorage.setItem(STORAGE_KEY, String(valid));
    setIntervalState(valid);
  };

  return [interval, setInterval];
}
